<template>
  <PasswordRecoveryFrame
    :title="$t(isSuccess ? 'auth.email-sent' : 'recovery.request-title')"
    :description="$t(isSuccess ? 'recovery.sent-description' : 'recovery.request-description')"
    :focus-title="isSuccess"
    :icon="isSuccess ? 'check' : 'link'"
  >
    <template v-if="isSuccess">
      <div
        class="recovery-card"
        role="status"
        data-test="recovery-success"
      >
        <p class="recovery-copy">
          {{ $t('recovery.sent-to') }}
        </p>
        <strong class="recovery-address">{{ sentEmail }}</strong>
        <p class="recovery-copy">
          {{ $t('recovery.check-spam') }}
        </p>
        <button
          type="button"
          class="recovery-text-action"
          :disabled="isLoading"
          @click="changeEmail"
        >
          {{ $t('recovery.change-email') }}
        </button>
      </div>
      <p
        v-if="error"
        class="recovery-error"
        role="alert"
      >
        {{ error }}
      </p>
      <button
        type="button"
        class="growth-button recovery-action"
        :disabled="isLoading || cooldown > 0"
        :aria-busy="isLoading"
        data-test="resend-link"
        @click="submitResetRequest"
      >
        <span
          v-if="isLoading"
          class="recovery-spinner"
          aria-hidden="true"
        />
        {{ $t(isLoading ? 'recovery.sending' : cooldown > 0 ? 'recovery.resend-countdown' : 'recovery.resend', { seconds: cooldown }) }}
      </button>
      <router-link
        :to="{ name: 'login' }"
        class="growth-button growth-button--lime recovery-action"
      >
        {{ $t('auth.back-to-login') }}<GrowthIcon name="arrow" />
      </router-link>
    </template>
    <form
      v-else
      class="tw:grid tw:gap-5"
      novalidate
      :aria-busy="isLoading"
      @submit.prevent="submitResetRequest"
    >
      <p
        v-if="route.query.recovery === 'invalid'"
        class="recovery-copy"
        role="status"
      >
        {{ $t('recovery.invalid-description') }}
      </p>
      <BaseInput
        id="recovery-email"
        v-model="email"
        :label="$t('auth.email')"
        type="email"
        data-test="email-input"
        autocomplete="email"
        inputmode="email"
        autocapitalize="none"
        :spellcheck="false"
        :disabled="isLoading"
        :has-error="!!emailError"
        :error-message="emailError"
        required
        @blur="validateEmail"
      />
      <p
        v-if="error"
        class="recovery-error"
        role="alert"
      >
        {{ error }}
      </p>
      <button
        type="submit"
        class="growth-button growth-button--lime recovery-action"
        :disabled="isLoading"
        data-test="reset-password-button"
      >
        <span
          v-if="isLoading"
          class="recovery-spinner"
          aria-hidden="true"
        />
        {{ $t(isLoading ? 'recovery.sending' : 'auth.send-reset-link') }}
        <GrowthIcon
          v-if="!isLoading"
          name="arrow"
        />
      </button>
      <p class="recovery-copy">
        {{ $t('recovery.email-help') }}
      </p>
    </form>
  </PasswordRecoveryFrame>
</template>
<script setup>
import { nextTick, onBeforeUnmount, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import BaseInput from '@/components/ui/BaseInput.vue';
import GrowthIcon from '@/components/growth/GrowthIcon.vue';
import PasswordRecoveryFrame from '@/components/auth/PasswordRecoveryFrame.vue';
import authService from '@/services/authService';
import { recoveryErrorKey } from '@/utils/passwordRecovery';
import { usePageSeo } from '@/composables/usePageSeo';

usePageSeo({ titleKey: 'auth.forgot-title', descriptionKey: 'auth.forgot-description' });
const { t } = useI18n();
const route = useRoute();
const email = ref('');
const sentEmail = ref('');
const emailError = ref('');
const isLoading = ref(false);
const error = ref('');
const isSuccess = ref(false);
const cooldown = ref(0);
let timer;
let active = true;
function validateEmail() {
  email.value = email.value.trim();
  emailError.value = !email.value ? t('errors.required')
    : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value) ? '' : t('errors.email-invalid');
  return !emailError.value;
}
function startCooldown() {
  clearInterval(timer);
  cooldown.value = 30;
  timer = setInterval(() => {
    cooldown.value = Math.max(0, cooldown.value - 1);
    if (!cooldown.value) clearInterval(timer);
  }, 1000);
}
async function changeEmail() {
  isSuccess.value = false;
  error.value = '';
  await nextTick();
  document.getElementById('recovery-email')?.focus();
}
async function submitResetRequest() {
  if (isLoading.value || (isSuccess.value && cooldown.value > 0)) return;
  if (!validateEmail()) {
    await nextTick();
    document.getElementById('recovery-email')?.focus();
    return;
  }
  isLoading.value = true;
  error.value = '';
  try {
    await authService.getPasswordResetLink({ email: email.value });
    if (!active) return;
    sentEmail.value = email.value;
    isSuccess.value = true;
    startCooldown();
  } catch (err) {
    if (!active) return;
    // The same confirmation avoids revealing whether an email owns an account.
    if (err.response?.status === 404) {
      sentEmail.value = email.value;
      isSuccess.value = true;
      startCooldown();
    } else {
      error.value = t(recoveryErrorKey(err));
    }
  } finally {
    if (active) isLoading.value = false;
  }
}
onBeforeUnmount(() => { active = false; clearInterval(timer); });
</script>
